import { useState, useCallback } from 'react';
import { MapPresenter } from '../MapPresenter';
import { FeatureInfoStore, FeatureWindowPresentation } from '../Map.types';
import { MapSettingsService } from '../services/MapSettingsService';
import { FloatingWindowDimensions, UserI } from '../../../components/sitebase/Base.types';

export interface UseFeatureInfoReturn {
    data: FeatureInfoStore;
    windowVisible: boolean;
    dimensions: FloatingWindowDimensions;
    presentation: FeatureWindowPresentation;

    set: (info: FeatureInfoStore) => void;
    setWindowVisible: (visible: boolean) => void;
    clearAllFeatures: () => void;
    onDimensionChange: (dimensions: FloatingWindowDimensions) => void;
    onPresentationChange: (presentation: FeatureWindowPresentation) => void;
}

export function useFeatureInfo(
    presenter: MapPresenter,
    remote: MapSettingsService,
    webSettings: UserI['web_settings'],
): UseFeatureInfoReturn {
    const [store, setStore] = useState<FeatureInfoStore>({});
    const [windowVisible, setWindowVisible] = useState<boolean>(false);
    const [dimensions, setDimensions] = useState<FloatingWindowDimensions>(
        webSettings.feature_info_window_dimensions
    );
    const [presentation, setPresentation] = useState<FeatureWindowPresentation>(
        webSettings.feature_info_presentation
    );

    const set = useCallback((info: FeatureInfoStore) => {
        setStore(info);
    }, []);

    const clear = useCallback(() => {
        console.debug("Clearing features");
        const newFeatureInfos = presenter.removeAllFeatureInfos();
        setStore(newFeatureInfos);
        remote.updateUserWebSettings({ active_map_pins: {} });
    }, [presenter, remote]);

    const onDimensionChange = useCallback((dimensions: FloatingWindowDimensions) => {
        console.debug("FeatureInfo dimension change", JSON.stringify(dimensions));
        setDimensions(dimensions);
        remote.updateUserWebSettings({ feature_info_window_dimensions: dimensions });
    }, [remote]);

    const onPresentationChange = useCallback((presentation: FeatureWindowPresentation) => {
        console.debug("Feature Info presentation change", presentation);
        setPresentation(presentation);
        remote.updateUserWebSettings({ feature_info_presentation: presentation });
    }, [remote]);

    return {
        data: store,
        windowVisible,
        dimensions,
        presentation,
        set,
        setWindowVisible,
        clearAllFeatures: clear,
        onDimensionChange,
        onPresentationChange,
    };
}
